import React from "react";
import CookieConsent from "react-cookie-consent";

const COOKIE_NAME = "myAwesomeCookieName2";
const CLARITY_PROJECT_ID = process.env.REACT_APP_MICROSOFT_CLARITY_ID || "wqegb5yos";
const GOOGLE_TAG_MANAGER_ID = process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID || "GTM-W5JPH2W9";

type DataLayerItem = Record<string, unknown>;
type ClarityFunction = ((...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
  interface Window {
    clarity?: ClarityFunction;
    dataLayer?: DataLayerItem[];
  }
}

function hasAcceptedCookieConsent(): boolean {
  const consentCookie = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${COOKIE_NAME}=`));

  if (!consentCookie) {
    return false;
  }

  return decodeURIComponent(consentCookie.split("=")[1] || "") === "true";
}

function appendScript(id: string, src: string): void {
  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.id = id;
  script.src = src;
  document.head.appendChild(script);
}

function loadGoogleTagManager(): void {
  if (document.getElementById("or-google-tag-manager")) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "gtm.js",
    "gtm.start": new Date().getTime(),
  });

  appendScript(
    "or-google-tag-manager",
    `https://www.googletagmanager.com/gtm.js?id=${GOOGLE_TAG_MANAGER_ID}`,
  );
}

function loadMicrosoftClarity(): void {
  if (document.getElementById("or-microsoft-clarity")) {
    return;
  }

  if (!window.clarity) {
    const clarityFunction: ClarityFunction = (...args: unknown[]) => {
      clarityFunction.q = clarityFunction.q || [];
      clarityFunction.q.push(args);
    };

    window.clarity = clarityFunction;
  }

  appendScript("or-microsoft-clarity", `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`);
}

function loadMarketingPixels(): void {
  if (process.env.NODE_ENV !== "production") {
    return;
  }

  loadGoogleTagManager();
  loadMicrosoftClarity();
}

export function MarketingTrackingConsent(): React.ReactElement {
  React.useEffect(() => {
    if (hasAcceptedCookieConsent()) {
      loadMarketingPixels();
    }
  }, []);

  return (
    <CookieConsent
      buttonClasses="marketing-pre-register__cookie-button"
      buttonText="Aceptar"
      buttonWrapperClasses="marketing-pre-register__cookie-actions"
      containerClasses="marketing-pre-register__cookie-consent"
      contentClasses="marketing-pre-register__cookie-content"
      cookieName={COOKIE_NAME}
      declineButtonClasses="marketing-pre-register__cookie-button marketing-pre-register__cookie-button--decline"
      declineButtonText="Rechazar"
      disableStyles
      enableDeclineButton
      expires={150}
      location="bottom"
      onAccept={loadMarketingPixels}
    >
      Esta web utiliza cookies técnicas, de personalización y análisis, propias y de terceros, que tratan datos de
      conexión y/o del dispositivo para facilitarle la navegación y analizar estadísticas del uso de la web.{" "}
      <a
        className="marketing-pre-register__cookie-link"
        href="https://openreferences.com/politica-de-cookies/"
        target="_blank"
        rel="noreferrer"
      >
        Obtener más información.
      </a>
      .
    </CookieConsent>
  );
}
